import React, { createContext, useCallback, useState, ReactNode } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Image, Card } from "@heroui/react";
import { useRouter } from 'next/navigation';
import { ROUTES } from '~/constants/routes.contants';
import { useTranslation } from 'react-i18next';
import { useEvent } from '~/hooks/useEvent';

interface AuthModalContextProps {
  showAuthModal: () => void;
  hideAuthModal: () => void;
}

export const AuthModalContext = createContext<AuthModalContextProps | undefined>(undefined);

interface AuthModalProviderProps {
  children: ReactNode;
}

export const AuthModalProvider: React.FC<AuthModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const { sendEvent } = useEvent();
  const { t } = useTranslation();

  const showAuthModal = useCallback(() => {
    sendEvent('auth_modal_open');
    setIsOpen(true);
  }, [sendEvent]);

  const hideAuthModal = useCallback(() => {
    sendEvent('auth_modal_close');
    setIsOpen(false);
  }, [sendEvent]);

  const handleLogin = () => {
    sendEvent('auth_modal_login_button_click');
    hideAuthModal();
    router.push(ROUTES.AUTH.LOGIN);
  };

  const benefits = [
    'follow',
    'review',
    'save',
    'community',
    'personalized'
  ];

  return (
    <AuthModalContext.Provider value={{ showAuthModal, hideAuthModal }}>
      {children}
      <Modal
        isOpen={isOpen}
        onClose={hideAuthModal}
        placement="center"
      >
        <ModalContent className="bg-background">
          <ModalHeader className="flex flex-col gap-1 border-b border-secondary-100">
            <h2 className="text-2xl font-bold text-center text-foreground-900">{t('auth.required')}</h2>
            <p className="text-secondary-600 text-center text-small">
              {t('auth.actionRequiresAuth')}
            </p>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              <Card className="border-none rounded-none">
                <Image
                  removeWrapper
                  alt="Food community"
                  className="z-0 w-full object-cover max-h-[200px] rounded-none"
                  src="/images/auth/table.png"
                />
              </Card>

              <div className="space-y-3 px-4 text-center">
                {benefits.map((benefit) => (
                  <p
                    key={benefit}
                    className="text-foreground font-medium text-medium"
                  >
                    {t(`auth.benefits.${benefit}`)}
                  </p>
                ))}
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="flex justify-between border-t border-secondary-100">
            <Button
              variant="light"
              color="secondary"
              onPress={hideAuthModal}
              className="w-32"
            >
              {t('auth.cta.cancel')}
            </Button>
            <Button
              color="warning"
              onPress={handleLogin}
              className="w-32"
            >
              {t('auth.cta.login')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AuthModalContext.Provider>
  );
}; 